import React, { useEffect, useState } from "react";
import { Box, Typography, Chip, Paper } from "@mui/material";
import CustomDataGrid from "../../Components/CustomDataGrid";
import { REACT_APP_BACKEND_URL } from "../../Utils/Language/urlBase";
import { toast } from "react-toastify";
import useAuthHeader from "react-auth-kit/hooks/useAuthHeader";
import WarningIcon from "@mui/icons-material/Warning";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import { useLang } from "../../context/LanguageContext";
import { formatDate } from "../../Utils/dateFormatter";
import { getExpiringStockItems } from "../../Utils/expiringStock";

const ItemsExpiringSoon = () => {
    const [expiringItems, setExpiringItems] = useState([]);
    const authHeader = useAuthHeader();
    const { t } = useLang();

    const fetchItems = async () => {
        try {
            const response = await fetch(`${REACT_APP_BACKEND_URL}/items`, {
                method: "GET",
                headers: {
                    Authorization: authHeader,
                    "Content-Type": "application/json",
                },
            });
            if (response.ok) {
                const responseData = await response.json();
                const items = responseData.data;
                const expiringStockItems = getExpiringStockItems(items);
                setExpiringItems(expiringStockItems);
            } else {
                throw new Error("Failed to fetch items.");
            }
        } catch (error) {
            toast.error(error.message || "Error fetching items.");
        }
    };

    useEffect(() => {
        fetchItems();
    }, [authHeader]);

    const columns = [
        {
            field: "name",
            headerName: "Item Name",
            minWidth: 200,
            flex: 1,
        },
        {
            field: "barcode",
            headerName: "Barcode",
            minWidth: 120,
            flex: 1,
        },
        {
            field: "quantityRemaining",
            headerName: "Qty Remaining",
            minWidth: 120,
            flex: 1,
        },
        {
            field: "pricePurchase",
            headerName: "Purchase Price",
            minWidth: 120,
            flex: 1,
            valueFormatter: (value) => {
                const num = parseFloat(value);
                return isNaN(num) ? "N/A" : `$${num.toFixed(2)}`;
            },
        },
        {
            field: "expiryDate",
            headerName: "Expiry Date",
            minWidth: 120,
            flex: 1,
            valueGetter: (expiryDate) => {
                return formatDate(expiryDate);
            },
        },
        {
            field: "daysUntilExpiry",
            headerName: "Days Until Expiry",
            minWidth: 150,
            flex: 1,
            renderCell: (params) => {
                const days = params.row.daysUntilExpiry;
                let color = "success";
                if (days <= 30) {
                    color = "error";
                } else if (days <= 45) {
                    color = "warning";
                }
                return (
                    <Chip
                        label={`${days} days`}
                        color={color}
                        size="small"
                        icon={days <= 30 ? <WarningIcon /> : undefined}
                    />
                );
            },
        },
        {
            field: "stockLocationName",
            headerName: "Stock Location",
            minWidth: 150,
            flex: 1,
        },
        {
            field: "otherPartyName",
            headerName: "Supplier",
            minWidth: 150,
            flex: 1,
        },
    ];

    const criticalCount = expiringItems.filter(
        (i) => i.daysUntilExpiry <= 30,
    ).length;
    const warningCount = expiringItems.filter(
        (i) => i.daysUntilExpiry > 30 && i.daysUntilExpiry <= 45,
    ).length;

    return (
        <Box sx={{ width: "100%" }}>
            {/* Modern Page Header */}
            <Paper
                elevation={0}
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 3,
                    p: 2.5,
                    borderRadius: 3,
                    background:
                        "linear-gradient(135deg, #00897b 0%, #004d40 100%)",
                    color: "white",
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <HourglassTopIcon sx={{ fontSize: 32, opacity: 0.9 }} />
                    <Box>
                        <Typography variant="h5" fontWeight={700}>
                            {t("ITEMS_EXPIRING_SOON")}
                        </Typography>
                        <Box sx={{ display: "flex", gap: 1, mt: 0.5 }}>
                            <Chip
                                label={`${expiringItems.length} ${t("ITEMS")}`}
                                size="small"
                                sx={{
                                    bgcolor: "rgba(255,255,255,0.2)",
                                    color: "white",
                                    fontWeight: 600,
                                }}
                            />
                            {criticalCount > 0 && (
                                <Chip
                                    label={`⚠ ${criticalCount} ≤30 days`}
                                    size="small"
                                    sx={{
                                        bgcolor: "rgba(229,57,53,0.8)",
                                        color: "white",
                                        fontWeight: 600,
                                    }}
                                />
                            )}
                            {warningCount > 0 && (
                                <Chip
                                    label={`${warningCount} 31-45 days`}
                                    size="small"
                                    sx={{
                                        bgcolor: "rgba(255,160,0,0.8)",
                                        color: "white",
                                        fontWeight: 600,
                                    }}
                                />
                            )}
                        </Box>
                    </Box>
                </Box>
            </Paper>
            <CustomDataGrid
                columns={columns}
                rows={expiringItems}
                perPage={20}
            />
        </Box>
    );
};

export default ItemsExpiringSoon;
